import { GlobalState, GlobalStateContext } from "GlobalState"
import classNames from "classnames"
import { FC, ReactElement, useContext } from "react"
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom"

// routes
import { BottomNavBar } from "components/BottomNavBar"
import { AnimatePresence, motion } from "framer-motion"
import { AppCollectionPage } from "pages/AppCollectionPage"
import { AppInfoPage } from "pages/AppInfoPage"
import { DiscoverPage } from "pages/DiscoverPage"
import { Error404Page } from "pages/Error404Page"
import { PlayPage } from "pages/Play/PlayPage"
import { SearchPage } from "pages/SearchPage"
import { AboutPage } from "pages/Settings/AboutPage"
import { OpenSourceLicensePage } from "pages/Settings/OpenSourceLicensePage"
import { PrivacyPolicyPage } from "pages/Settings/PrivacyPolicyPage"
import { TermsOfServicePage } from "pages/Settings/TermsOfServicePage"

import MainFTUE from "components/MainFTUE"
import SettingsMenu from "components/SettingsMenu"
import DownloadOverWifiModal from "components/modals/DownloadOverWifiModal"
import InstallPermissionsModal from "components/modals/InstallPermissionsModal"
import InsufficientStorageModal from "components/modals/InsufficientStorageModal"
import UnknownErrorModal from "components/modals/UnknownErrorModal"
import colors from "styles/colors"
import "./Modal"

interface AnimatedPageProps {
  el: ReactElement
  navBar?: boolean
}

const AnimatedPage: FC<AnimatedPageProps> = ({ el, navBar = true }) => {
  const {
    scrollRestorationRoutes,
    setScrollRestorationRoute,
    scrollContainerRef,
    shouldScroll,
    setShouldScroll,
    setShowBottomNavBar,
  } = useContext(GlobalStateContext)

  const location = useLocation()

  function onAnimationStart(e: any) {
    if (e.opacity === 1) {
      // Page entering - restore scroll pos
      setTimeout(() => {
        const scrollY = scrollRestorationRoutes[location.pathname] || 0
        scrollContainerRef.current.scrollTo(0, scrollY)
      }, 1)

      setShowBottomNavBar(navBar)
      setShouldScroll(true) // Restore scroll settings
    } else {
      // Page exited - store scroll pos for later
      let { scrollTop } = scrollContainerRef.current
      if (scrollTop < 20) scrollTop = null
      setScrollRestorationRoute(location.pathname, scrollTop)
    }
  }

  return (
    <motion.div
      ref={scrollContainerRef}
      className={classNames("animatedRouteWrapper", {
        withoutScroll: !shouldScroll,
      })}
      initial={{ opacity: 0, transform: "translateX(1rem)" }}
      animate={{ opacity: 1, transform: "translateX(0rem)" }}
      exit={{ opacity: 0, transform: "translateX(-1rem)" }}
      transition={{ duration: 0.25 }}
      onAnimationStart={onAnimationStart}
    >
      {el}
    </motion.div>
  )
}

const AnimatedRouter = () => {
  const location = useLocation()

  // Routes
  const routes = [
    { path: "discover", el: <DiscoverPage /> },
    { path: "play", el: <PlayPage /> },
    { path: "search", el: <SearchPage /> },
    { path: "app/:appId", el: <AppInfoPage />, navBar: false },
    { path: "app_collection/:appCollectionId", el: <AppCollectionPage /> },

    // settings
    { path: "terms_of_service", el: <TermsOfServicePage />, navBar: false },
    { path: "privacy_policy", el: <PrivacyPolicyPage />, navBar: false },
    { path: "about", el: <AboutPage />, navBar: false },
    {
      path: "open_source_license",
      el: <OpenSourceLicensePage />,
      navBar: false,
    },

    // other
    { path: "*", el: <Error404Page /> },
  ]

  return (
    <div className="page-content">
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={<Navigate to="/play" replace={true} />}
          />

          {routes.map(({ path, el, navBar }) => (
            <Route
              key={path}
              path={path}
              element={<AnimatedPage el={el} navBar={navBar} />}
            />
          ))}
        </Routes>
      </AnimatePresence>
    </div>
  )
}

const Modals: FC = () => {
  const { currentModal, currentModalParams = {} } =
    useContext(GlobalStateContext)

  if (!currentModal) return null

  return (
    <>
      {currentModal === "InstallPermissions" && (
        <InstallPermissionsModal {...currentModalParams} />
      )}
      {currentModal === "InsufficientStorage" && (
        <InsufficientStorageModal {...currentModalParams} />
      )}
      {currentModal === "DownloadOverWifi" && (
        <DownloadOverWifiModal {...currentModalParams} />
      )}
      {currentModal === "UnknownErrorModal" && (
        <UnknownErrorModal {...currentModalParams} />
      )}
    </>
  )
}

const AppBase: FC = () => {
  const { mainFTUECompleted } = useContext(GlobalStateContext)
  // const statusBarBackground = utils.isDev()
  //   ? colors.yellow
  //   : utils.isStaging()
  //   ? colors.pink
  //   : colors.backgroundDark

  const statusBarBackground = colors.backgroundDark

  return (
    <AnimatePresence mode="wait">
      {mainFTUECompleted ? (
        <>
          {/* Status Bar Spacing */}
          <div
            style={{
              height: 30,
              background: statusBarBackground,
              boxShadow: `0 0 0 2px ${colors.backgroundDark}`,
              zIndex: 999999,
            }}
          ></div>

          <AnimatedRouter />
          <BottomNavBar />
          <SettingsMenu />
          <Modals />
        </>
      ) : (
        <motion.div
          key="mainFTUE"
          initial={{ opacity: 1, height: "100%" }}
          exit={{ opacity: 0, transform: "translateX(-1rem)" }}
          transition={{ duration: 0.5 }}
        >
          <MainFTUE />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <GlobalState>
        <AppBase />
      </GlobalState>
    </BrowserRouter>
  )
}
